import React from "react";
import { Paper, Stack, Typography, Box, Skeleton } from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import { ResponsiveBar } from "@nivo/bar";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "../CareManager/CMStatCards";
import SUDTable from "./SUDTable";

/**
 * Panel 3: Substance Use & Co-occurring Disorders.
 * 4 SUD stat cards + SUDTable + Condition Prevalence horizontal bar chart.
 */
export default function PanelSubstanceUse({ data, loading, onCreateTask, helpContent }) {
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Skeleton variant="rectangular" animation="wave" height={350} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const patients = data?.patients || [];
  const conditionPrevalence = data?.conditionPrevalence || [];
  const hasPatients = patients.length > 0;

  const statItems = [
    { label: "Drug Use Disorders", value: data?.drugUseDisorders, color: "#DC2626", bgColor: "#FEE2E2" },
    { label: "Alcohol Use", value: data?.alcoholUseDisorders, color: "#D97706", bgColor: "#FEF3C7" },
    { label: "Opioid Disorder", value: data?.opioidDisorder, color: "#BE185D", bgColor: "#FCE7F3" },
    { label: "Tobacco Use", value: data?.tobaccoUse, color: "#7C3AED", bgColor: "#F3E8FF" },
  ];

  // Horizontal bar data (top 8 conditions)
  const barData = conditionPrevalence.slice(0, 8).map((c) => ({
    condition: c.condition.length > 25 ? c.condition.slice(0, 22) + "..." : c.condition,
    count: c.count,
    pct: c.pct,
    fullName: c.condition,
  }));

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: "#FCE7F3", color: "#BE185D",
            }}
          >
            <LocalPharmacyIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Substance Use &amp; Co-occurring</Typography>
            <Typography variant="caption" color="text.secondary">Substance use disorders and behavioral comorbidities</Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Content: Table + Bar Chart side by side on large screens */}
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" }, alignItems: "start" }}>
          {/* SUD Patient Table */}
          {hasPatients ? (
            <SUDTable patients={patients} onCreateTask={onCreateTask} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}>
              No substance use disorder patients
            </Typography>
          )}

          {/* Condition Prevalence Bar Chart */}
          {barData.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1, display: "block" }}>
                Top BH Conditions
              </Typography>
              <Box sx={{ height: 280 }}>
                <ResponsiveBar
                  data={barData}
                  keys={["count"]}
                  indexBy="condition"
                  layout="horizontal"
                  margin={{ top: 5, right: 50, bottom: 5, left: 140 }}
                  padding={0.3}
                  colors={["#7C3AED"]}
                  borderRadius={3}
                  enableGridX
                  enableGridY={false}
                  axisBottom={null}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 8,
                  }}
                  labelSkipWidth={20}
                  label={(d) => `${d.value}`}
                  labelTextColor="#FFFFFF"
                  tooltip={({ data: d }) => (
                    <Paper sx={{ px: 1.5, py: 0.8, fontSize: "0.75rem" }}>
                      <strong>{d.fullName || d.condition}</strong>: {d.count} ({d.pct}%)
                    </Paper>
                  )}
                  theme={{
                    axis: { ticks: { text: { fontSize: 11 } } },
                    labels: { text: { fontSize: 11, fontWeight: 700 } },
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
